import { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { successStoriesAPI } from '../lib/api';
import { Trophy, TrendingUp, DollarSign } from 'lucide-react';

export const SuccessStoriesPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const res = await successStoriesAPI.getAll();
        setStories(res.data);
      } catch (err) {
        console.error('Error fetching success stories:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  const defaultStories = [
    {
      id: '1',
      company_name: 'Jive Software',
      story_type: 'exit',
      title: 'Acquired for $462M',
      description: 'Portland-based enterprise collaboration software company acquired by Aurea Software after going public.',
      year: 2017,
      valuation: '$462 million',
    },
    {
      id: '2',
      company_name: 'Puppet',
      story_type: 'acquisition',
      title: 'Acquired by Perforce',
      description: 'Infrastructure automation leader acquired after becoming a staple of the DevOps ecosystem.',
      year: 2022,
      valuation: 'Undisclosed',
    },
    {
      id: '3',
      company_name: 'Elemental Technologies',
      story_type: 'acquisition',
      title: 'Acquired by Amazon',
      description: 'Video processing company acquired by Amazon, later becoming part of AWS Elemental.',
      year: 2015,
      valuation: '$500 million',
    },
    {
      id: '4',
      company_name: 'Simple',
      story_type: 'acquisition',
      title: 'Acquired by BBVA',
      description: 'Online banking startup acquired by Spanish bank BBVA, pioneering digital banking.',
      year: 2014,
      valuation: '$117 million',
    },
    {
      id: '5',
      company_name: 'Urban Airship',
      story_type: 'growth',
      title: 'Mobile Engagement Leader',
      description: 'Mobile engagement platform serving billions of push notifications, rebranded as Airship.',
      year: 2020,
      valuation: 'Series D funded',
    },
  ];

  const displayStories = stories.length > 0 ? stories : defaultStories;

  const getTypeIcon = (type) => {
    switch (type) {
      case 'exit':
      case 'ipo':
        return TrendingUp;
      case 'unicorn':
        return Trophy;
      default:
        return DollarSign;
    }
  };

  return (
    <div data-testid="success-stories-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Achievements</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Success Stories</h1>
          <p className="text-muted-foreground max-w-2xl">
            Portland's tech ecosystem has produced numerous successful exits, 
            acquisitions, and notable achievements. Here are some highlights.
          </p>
        </div>
      </section>

      {/* Stories */}
      <section>
        <div className="container-wide py-12">
          {loading ? (
            <div className="space-y-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="border border-border p-6 animate-pulse">
                  <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                  <div className="h-6 bg-muted rounded w-1/2 mb-2" />
                  <div className="h-3 bg-muted rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {displayStories.map((story, index) => {
                const Icon = getTypeIcon(story.story_type);
                return (
                  <Card 
                    key={story.id}
                    className="card-hover animate-slide-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`success-story-${story.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-6">
                        <div className="w-16 h-16 rounded-sm bg-muted flex items-center justify-center">
                          <Icon className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="badge-category capitalize">{story.story_type}</span>
                            <span className="text-sm text-muted-foreground">{story.year}</span>
                          </div>
                          <h3 className="font-semibold text-xl mb-1">{story.company_name}</h3>
                          <h4 className="text-lg text-primary mb-3">{story.title}</h4>
                          <p className="text-muted-foreground mb-4">{story.description}</p>
                          {story.valuation && (
                            <div className="flex items-center gap-2 text-sm">
                              <DollarSign className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{story.valuation}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-muted/30">
        <div className="container-wide py-16">
          <div className="text-center max-w-2xl mx-auto">
            <Trophy className="w-12 h-12 mx-auto mb-6 text-muted-foreground" />
            <h2 className="text-heading text-2xl mb-4">Know a Success Story?</h2>
            <p className="text-muted-foreground">
              If you know of a Portland tech success story we should feature, 
              please let us know. We love celebrating our community's achievements.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
